
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Action, Getter } from 's-vuex-class'
import {
    Card,
    CardSectionIndex,
    CardIndex,
    HasCard,
    HasSection,
    isBranchedCard,
    isScorecardCard,
    isListCard,
} from '../../../entities/conversation'
import { blankCard } from '../../../transforms/conversation'
import { ContextualData } from '../ConversationValidator'
import CardMarshaller from './CardMarshaller.vue'
import DropMenu from './DropMenu.vue'
import { Dict } from '../../../entities/object'

type MenuAction = 'rename' | 'remove'

@Component<CardSection>({
    components: {
        CardMarshaller,
        DropMenu,
    },
})
export default class CardSection extends Vue {
    @Prop(Boolean) public hasScorecardFeature!: boolean
    @Prop(Number) public topicKeyUsed!: number
    @Prop(String) public name!: string
    @Prop({ type: Boolean, default: false }) public leadQuestion!: boolean
    @Prop(Array) public cards!: Card[]
    @Prop(Boolean) public flow!: boolean
    @Prop(Object) public sectionIndex!: CardSectionIndex

    @Getter public getContextual!: Dict<ContextualData>
    @Getter $companyVars

    @Getter public getSurvey
    @Getter public getInSubAppDesignTab?: boolean

    @Action public insertCard!: (data: CardIndex & HasCard) => void
    @Action public setSection!: (data: CardSectionIndex & HasSection) => void
    @Action public removeSection!: (data: CardSectionIndex) => void

    public isEditingName = false
    public editingName = ''
    public sectionMenuActions: MenuAction[] = ['rename', 'remove']

    public mounted() {
        if (this.name === '') {
            this.isEditingName = true
        }
    }

    public get getName() {
        if (this.getInSubAppDesignTab && this.sectionIndex.sectionIndex === 0) {
            return 'additional questions'
        }
        return this.name || ''
    }

    public get siblingsCount() {
        return this.cards.length
    }

    public get canEdit() {
        return (
            ['start', 'finish'].indexOf(this.name) === -1 && !this.leadQuestion
        )
    }

    public get context(): ContextualData | undefined {
        return this.getContextual[String(this.sectionIndex.sectionIndex)]
    }

    public get errors() {
        return (
            (this.context &&
                this.context.errors &&
                this.context.errors.section) ||
            {}
        )
    }

    public editName() {
        if (this.canEdit) {
            this.isEditingName = true
            this.editingName = String(this.name)
        }
    }

    public addCard() {
        // need to leave the last card alone if in a flow,
        // as it's the return jump
        const length = this.cards.length
        this.insertCard({
            ...this.sectionIndex,
            cardIndex: this.flow ? length - 1 : length,
            card: blankCard('unknown'),
        })
    }

    public getCardIndex(index: number) {
        return { ...this.sectionIndex, cardIndex: index }
    }

    public cancelName() {
        if (!this.isEditingName) {
            return
        }

        if (this.editingName === '' && this.cards.length === 0) {
            this.removeSection(this.sectionIndex)
        }
        this.isEditingName = false
    }

    public saveName() {
        if (!this.isEditingName) {
            return
        } // @todo uniqueness constraint
        if (this.editingName && this.editingName.trim()) {
            this.setSection({
                ...this.sectionIndex,
                section: {
                    cards: this.cards,
                    flow: this.flow,
                    name: this.editingName.toLowerCase().trim() || '',
                },
            })
        }
        this.isEditingName = false
    }

    public confirmRemoveSection() {
        if (confirm('Are you sure you want to remove this section?')) {
            this.removeSection({ ...this.sectionIndex })
        }
    }

    public handleSectionMenuAction(action: MenuAction) {
        switch (action) {
            case 'remove':
                this.confirmRemoveSection()
                break
            case 'rename':
                this.editName()
        }
    }

    // todo scorecard3 remove after scorecard is migrated completely
    // this is specifically for scorecard, we show either old or new scorecard card here based on
    // the toggle on/off
    shouldShowCard(card: Card) {
        // check if is scorecard, should be list && should case by rating, should have badgeId set
        if (
            this.$companyVars.has_scorecard_v3 &&
            isBranchedCard(card) &&
            (!isScorecardCard(card) || !card.scorecardId) && // not new version scorecard
            isListCard(card) &&
            card.match === 'rating' &&
            card.cases.find((cardCase) =>
                cardCase?.card?.options?.find((option) => !!option?.badgeId)
            )
        ) {
            return false
        }

        // hide v3 scorecard card if toggle not on
        if (
            !this.$companyVars.has_scorecard_v3 &&
            isScorecardCard(card) &&
            card.scorecardId
        ) {
            return false
        }

        return true
    }
}
